"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MAX_COLUMN_VALUE_ENTRIES = void 0;
exports.isNumberColumnValueStats = isNumberColumnValueStats;
exports.isColumnValuesEntry = isColumnValuesEntry;
exports.hasReliableColumnValueEntries = hasReliableColumnValueEntries;
exports.subjectIsFragment = subjectIsFragment;
exports.subjectIsGeography = subjectIsGeography;
exports.combineRasterBandStats = combineRasterBandStats;
exports.capColumnValueEntries = capColumnValueEntries;
exports.numberColumnStatsFromEntries = numberColumnStatsFromEntries;
exports.stringOrBooleanColumnStatsFromEntries = stringOrBooleanColumnStatsFromEntries;
exports.combineNumberColumnValueStats = combineNumberColumnValueStats;
exports.combineStringOrBooleanColumnValueStats = combineStringOrBooleanColumnValueStats;
exports.hashMetricDependency = hashMetricDependency;
exports.combineMetricsForFragments = combineMetricsForFragments;
exports.extractMetricDependenciesFromReportBody = extractMetricDependenciesFromReportBody;
const simple_statistics_1 = require("simple-statistics");
const uniqueIdIndex_1 = require("../utils/uniqueIdIndex");
/**
 * Downsamples a histogram of [value, count] pairs to a maximum number of
 * entries, preserving the overall distribution across the full value range.
 * This mirrors the approach used in rasterStats downsampling.
 */
function downsampleColumnHistogram(histogram, maxEntries) {
    if (histogram.length === 0 || histogram.length <= maxEntries) {
        return histogram;
    }
    const sorted = [...histogram].sort((a, b) => a[0] - b[0]);
    const minValue = sorted[0][0];
    const maxValue = sorted[sorted.length - 1][0];
    if (!isFinite(minValue) || !isFinite(maxValue) || minValue === maxValue) {
        const totalCount = sorted.reduce((acc, [, count]) => acc + count, 0);
        return [[minValue, totalCount]];
    }
    const numBins = maxEntries;
    const binCounts = new Array(numBins).fill(0);
    const span = maxValue - minValue;
    for (const [value, count] of sorted) {
        const normalized = (value - minValue) / span;
        let binIndex = Math.round(normalized * (numBins - 1));
        if (binIndex < 0)
            binIndex = 0;
        if (binIndex >= numBins)
            binIndex = numBins - 1;
        binCounts[binIndex] += count;
    }
    const result = [];
    for (let i = 0; i < numBins; i++) {
        const count = binCounts[i];
        if (count === 0)
            continue;
        const value = minValue + (span * i) / (numBins - 1);
        result.push([value, count]);
    }
    return result;
}
/**
 * Maximum number of per-feature entries retained on a single column's stats.
 * When a fragment sees more features than this, entries are dropped entirely
 * (a partial list is useless for exact merging) and `entriesTruncated` is
 * set; combining across fragments then falls back to approximate stat
 * merging. Exactness matters most when feature counts are small (e.g.
 * summing populations of a handful of districts); at large counts the
 * relative error from double-counting boundary-spanning features shrinks,
 * while the byte cost of entries grows, so a low cap is the right trade.
 */
exports.MAX_COLUMN_VALUE_ENTRIES = 300;
function isNumberColumnValueStats(stats) {
    return stats.type === "number";
}
/**
 * Type guard for {@link ColumnValuesEntry}. Accepts untrusted input (e.g.
 * metric values deserialized from the database).
 */
function isColumnValuesEntry(value) {
    if (value === null || typeof value !== "object") {
        return false;
    }
    const candidate = value;
    if (typeof candidate.id !== "number") {
        return false;
    }
    if (typeof candidate.value !== "number" &&
        typeof candidate.value !== "string" &&
        typeof candidate.value !== "boolean") {
        return false;
    }
    if (typeof candidate.weight !== "number") {
        return false;
    }
    if (!Array.isArray(candidate.offsets) ||
        candidate.offsets.some((o) => typeof o !== "number")) {
        return false;
    }
    return true;
}
/**
 * Returns true if the given column stats carry a complete (untruncated) set
 * of per-feature entries which can be used to exactly combine statistics
 * across fragments.
 */
function hasReliableColumnValueEntries(stats) {
    if (stats === null || typeof stats !== "object") {
        return false;
    }
    const candidate = stats;
    if (candidate.entriesTruncated === true) {
        return false;
    }
    if (!Array.isArray(candidate.entries)) {
        return false;
    }
    return candidate.entries.every(isColumnValuesEntry);
}
function subjectIsFragment(subject) {
    return subject != null && typeof subject === "object" && "hash" in subject;
}
function subjectIsGeography(subject) {
    return subject != null && typeof subject === "object" && "id" in subject;
}
function equalIntervalBuckets(data, numBuckets, max, fraction = false) {
    const breaks = (0, simple_statistics_1.equalIntervalBreaks)(data, numBuckets);
    breaks.pop();
    max = max !== undefined ? max : Math.max(...data);
    return breaksToBuckets(max, breaks, data, fraction);
}
function breaksToBuckets(max, breaks, values, fraction = false) {
    const buckets = [];
    for (const b of breaks) {
        const nextBreak = breaks[breaks.indexOf(b) + 1];
        const isLastBreak = nextBreak === undefined;
        let valuesInRange = 0;
        for (const value of values) {
            if (value >= b && (isLastBreak || value < nextBreak)) {
                valuesInRange++;
            }
        }
        buckets.push([b, fraction ? valuesInRange / values.length : valuesInRange]);
    }
    buckets.push([max, null]);
    return buckets;
}
/**
 * Combines RasterBandStats from multiple fragments into a single RasterBandStats.
 * Sums `count`, `sum`, and `invalid` across fragments; combined mean is `sum / count`.
 * Min/max are the extrema across fragments; histograms are merged and downsampled.
 *
 * @param statsArray - Array of RasterBandStats from different fragments
 * @returns Combined RasterBandStats, or undefined if the array is empty
 */
function combineRasterBandStats(statsArray) {
    if (statsArray.length === 0) {
        throw new Error("Cannot combine empty array of RasterBandStats");
    }
    if (statsArray.length === 1) {
        return statsArray[0];
    }
    // Combine counts and sums
    let totalCount = 0;
    let totalSum = 0;
    let totalInvalid = 0;
    const mins = [];
    const maxs = [];
    // All fragments of the same raster_stats metric share an epsg; pick the
    // first non-null value we encounter so it is preserved through combination.
    let combinedEpsg = undefined;
    // Merge histograms by value
    const histogramMap = new Map();
    for (const stats of statsArray) {
        totalCount += stats.count;
        if (isFinite(stats.sum)) {
            totalSum += stats.sum;
        }
        totalInvalid += stats.invalid;
        if (isFinite(stats.min) && stats.min !== null) {
            mins.push(stats.min);
        }
        if (isFinite(stats.max) && stats.max !== null) {
            maxs.push(stats.max);
        }
        if (combinedEpsg == null && stats.epsg != null) {
            combinedEpsg = stats.epsg;
        }
        // Merge histogram entries
        for (const [value, count] of stats.histogram) {
            histogramMap.set(value, (histogramMap.get(value) || 0) + count);
        }
    }
    // Convert histogram map back to array, sort by value, then downsample.
    // Each per-fragment histogram is capped at 200 entries, but combining them
    // via concatenation can produce 400+ entries. Downsample to keep the same
    // contract as the per-fragment histograms.
    const rawCombinedHistogram = Array.from(histogramMap.entries())
        .map(([value, count]) => [value, count])
        .sort((a, b) => a[0] - b[0]);
    const combinedHistogram = downsampleColumnHistogram(rawCombinedHistogram, 200);
    // Calculate combined mean using sum/count (not average of means)
    const combinedMean = totalCount > 0 ? totalSum / totalCount : NaN;
    // Calculate combined range
    const combinedMin = mins.length > 0 ? (0, simple_statistics_1.min)(mins) : NaN;
    const combinedMax = maxs.length > 0 ? (0, simple_statistics_1.max)(maxs) : NaN;
    const combinedRange = combinedMax - combinedMin;
    // For median, we can't easily combine without the full dataset, so we'll use NaN
    // or could potentially estimate from the combined histogram, but that's complex
    const combinedMedian = NaN;
    return {
        count: totalCount,
        min: combinedMin,
        max: combinedMax,
        mean: combinedMean,
        median: combinedMedian,
        range: combinedRange,
        histogram: combinedHistogram,
        invalid: totalInvalid,
        sum: totalSum,
        ...(combinedEpsg != null ? { epsg: combinedEpsg } : {}),
    };
}
/**
 * Applies the {@link MAX_COLUMN_VALUE_ENTRIES} cap to a set of entries.
 * When the cap is exceeded, entries are dropped entirely rather than
 * partially retained: a truncated list can never be used for exact merging
 * (see {@link hasReliableColumnValueEntries}), so storing part of it would
 * only add payload weight without any benefit.
 */
function capColumnValueEntries(entries) {
    if (entries.length <= exports.MAX_COLUMN_VALUE_ENTRIES) {
        return { entries, entriesTruncated: false };
    }
    return {
        entries: undefined,
        entriesTruncated: true,
    };
}
/**
 * Computes NumberColumnValueStats from per-feature entries. Each entry
 * represents a single original (pre-subdivision) feature, so counts, sums,
 * and distinct values are exact. Mean and stdDev are weighted by each
 * feature's overlap weight (area/length) when available.
 */
function numberColumnStatsFromEntries(entries) {
    const count = entries.length;
    if (count === 0) {
        return {
            type: "number",
            count: 0,
            min: NaN,
            max: NaN,
            mean: NaN,
            stdDev: NaN,
            histogram: [],
            countDistinct: 0,
            sum: 0,
        };
    }
    let minValue = Infinity;
    let maxValue = -Infinity;
    let sum = 0;
    let weightedSum = 0;
    let totalWeight = 0;
    const histogramMap = new Map();
    const distinctValues = new Set();
    for (const entry of entries) {
        const value = entry.value;
        if (typeof value !== "number") {
            continue;
        }
        distinctValues.add(value);
        const weight = entry.weight;
        if (value < minValue)
            minValue = value;
        if (value > maxValue)
            maxValue = value;
        sum += value;
        if (isFinite(weight) && weight > 0 && isFinite(value)) {
            weightedSum += value * weight;
            totalWeight += weight;
        }
        const histogramContribution = isFinite(weight) && weight > 0 ? weight : 1;
        histogramMap.set(value, (histogramMap.get(value) || 0) + histogramContribution);
    }
    const meanValue = totalWeight > 0 ? weightedSum / totalWeight : sum / count;
    let varianceNumerator = 0;
    if (totalWeight > 0) {
        for (const entry of entries) {
            const value = entry.value;
            const weight = entry.weight;
            if (typeof value !== "number" ||
                !isFinite(weight) ||
                weight <= 0) {
                continue;
            }
            const diff = value - meanValue;
            varianceNumerator += weight * diff * diff;
        }
        varianceNumerator = varianceNumerator / totalWeight;
    }
    else {
        for (const entry of entries) {
            const value = entry.value;
            if (typeof value !== "number") {
                continue;
            }
            const diff = value - meanValue;
            varianceNumerator += diff * diff;
        }
        varianceNumerator = varianceNumerator / count;
    }
    const stdDev = Math.sqrt(varianceNumerator);
    let histogram = Array.from(histogramMap.entries()).sort((a, b) => a[0] - b[0]);
    const MAX_HISTOGRAM_ENTRIES = 200;
    if (histogram.length > MAX_HISTOGRAM_ENTRIES) {
        histogram = downsampleColumnHistogram(histogram, MAX_HISTOGRAM_ENTRIES);
    }
    return {
        type: "number",
        count,
        min: minValue,
        max: maxValue,
        mean: meanValue,
        stdDev,
        histogram,
        countDistinct: distinctValues.size,
        sum,
        totalWeight: totalWeight > 0 ? totalWeight : undefined,
    };
}
/**
 * Reads a stats object's total overlap weight, falling back to the legacy
 * totalAreaSqKm field for metric values stored before the rename.
 */
function statsTotalWeight(stats) {
    return typeof stats.totalWeight === "number"
        ? stats.totalWeight
        : stats.totalAreaSqKm;
}
/**
 * Computes StringOrBooleanColumnValueStats from per-feature entries.
 */
function stringOrBooleanColumnStatsFromEntries(entries) {
    const distinctMap = new Map();
    let sawBoolean = false;
    let sawString = false;
    for (const entry of entries) {
        const value = entry.value;
        if (typeof value !== "string" && typeof value !== "boolean") {
            continue;
        }
        if (typeof value === "boolean") {
            sawBoolean = true;
        }
        else {
            sawString = true;
        }
        distinctMap.set(value, (distinctMap.get(value) ?? 0) + 1);
    }
    return {
        type: sawBoolean && !sawString ? "boolean" : "string",
        distinctValues: Array.from(distinctMap.entries()),
        countDistinct: distinctMap.size,
    };
}
/**
 * Merges per-feature entries from multiple fragments, deduplicating by
 * original feature id. Weights of the same feature are summed across
 * fragments (fragments are disjoint, so each fragment's clipped weight is a
 * distinct portion of the feature). Returns `sharedOffsets: true` when the
 * same subdivided part contributed to more than one fragment's stats, which
 * indicates the subjects overlapped (e.g. buffered fragments) and summed
 * weights may be overstated.
 */
function mergeColumnValueEntries(statsArray) {
    const byId = new Map();
    const seenOffsets = new Set();
    let sharedOffsets = false;
    for (const stats of statsArray) {
        const offsetsInThisFragment = new Set();
        for (const entry of stats.entries ?? []) {
            const existing = byId.get(entry.id);
            if (existing) {
                existing.weight += entry.weight;
                for (const offset of entry.offsets) {
                    if (!existing.offsets.includes(offset)) {
                        existing.offsets.push(offset);
                    }
                }
            }
            else {
                byId.set(entry.id, {
                    id: entry.id,
                    value: entry.value,
                    weight: entry.weight,
                    offsets: [...entry.offsets],
                });
            }
            for (const offset of entry.offsets) {
                offsetsInThisFragment.add(offset);
            }
        }
        for (const offset of offsetsInThisFragment) {
            if (seenOffsets.has(offset)) {
                sharedOffsets = true;
            }
            seenOffsets.add(offset);
        }
    }
    return { entries: Array.from(byId.values()), sharedOffsets };
}
/**
 * Combines ColumnValueStats from multiple fragments into a single ColumnValueStats.
 *
 * When every input carries a complete set of per-feature entries, statistics
 * are recomputed exactly from the merged entries, deduplicating features that
 * span multiple fragments (or were subdivided into multiple parts at upload
 * time) so values like `sum` are never double-counted.
 *
 * Otherwise falls back to approximate merging: if a total overlap weight is
 * available (totalWeight, or the legacy totalAreaSqKm field), mean and stdDev
 * are weighted by it; otherwise they are weighted by count.
 */
function combineNumberColumnValueStats(statsArray) {
    if (statsArray.length === 0) {
        return undefined;
    }
    if (statsArray.length === 1) {
        return statsArray[0];
    }
    if (statsArray.every(hasReliableColumnValueEntries)) {
        const { entries, sharedOffsets } = mergeColumnValueEntries(statsArray);
        // Stats are computed from the full merged entry set (exact); the cap
        // only limits what is retained for any further combination.
        const combined = numberColumnStatsFromEntries(entries);
        const capped = capColumnValueEntries(entries);
        if (capped.entries) {
            combined.entries = capped.entries;
        }
        if (capped.entriesTruncated) {
            combined.entriesTruncated = true;
        }
        if (sharedOffsets) {
            combined.weightsMayOverlap = true;
        }
        return combined;
    }
    // Determine whether to weight by overlap size (area/length) or by count
    const useOverlapWeight = statsArray.some((s) => {
        const weight = statsTotalWeight(s);
        return typeof weight === "number" && weight > 0;
    });
    let totalCount = 0;
    let totalSum = 0;
    let totalWeight = 0;
    const mins = [];
    const maxs = [];
    // For variance/stdDev combination we use:
    // E[x^2] = variance + mean^2, aggregated with the same weights as for the mean
    let weightedMeanNumerator = 0;
    let weightedSecondMoment = 0;
    // Merge histograms by value
    const histogramMap = new Map();
    for (const stats of statsArray) {
        const statsWeight = statsTotalWeight(stats);
        const weight = useOverlapWeight && typeof statsWeight === "number"
            ? Math.max(statsWeight, 0)
            : stats.count;
        if (!isFinite(weight) || weight <= 0) {
            continue;
        }
        totalCount += stats.count;
        totalSum += stats.sum;
        mins.push(stats.min);
        maxs.push(stats.max);
        if (isFinite(stats.mean)) {
            // Only include fragments with a finite mean in the weighted
            // mean/stdDev calculation. Fragments with NaN mean still
            // contribute to count/sum but are ignored for weighting.
            weightedMeanNumerator += stats.mean * weight;
            totalWeight += weight;
            if (isFinite(stats.stdDev)) {
                const variance = stats.stdDev * stats.stdDev;
                weightedSecondMoment += (variance + stats.mean * stats.mean) * weight;
            }
        }
        // Merge histogram entries
        for (const [value, count] of stats.histogram) {
            histogramMap.set(value, (histogramMap.get(value) || 0) + count);
        }
    }
    // If all weights were zero, fall back to simple stats if possible
    const combinedCount = totalCount;
    const combinedSum = totalSum;
    const combinedMin = mins.length > 0 ? (0, simple_statistics_1.min)(mins) : NaN;
    const combinedMax = maxs.length > 0 ? (0, simple_statistics_1.max)(maxs) : NaN;
    let combinedMean = NaN;
    let combinedStdDev = NaN;
    if (totalWeight > 0 && weightedMeanNumerator !== 0) {
        combinedMean = weightedMeanNumerator / totalWeight;
        if (weightedSecondMoment !== 0) {
            const meanSquare = weightedSecondMoment / totalWeight;
            const variance = meanSquare - combinedMean * combinedMean;
            combinedStdDev = Math.sqrt(Math.max(variance, 0));
        }
    }
    else if (combinedCount > 0) {
        combinedMean = combinedSum / combinedCount;
        // stdDev cannot be reliably combined without additional information; leave as NaN
    }
    // Convert histogram map back to array and sort by value
    let combinedHistogram = Array.from(histogramMap.entries())
        .map(([value, count]) => [value, count])
        .sort((a, b) => {
        if (typeof a[0] === "number" && typeof b[0] === "number") {
            return a[0] - b[0];
        }
        else {
            return 0;
        }
    });
    // Limit histogram size similarly to raster stats by downsampling
    const MAX_HISTOGRAM_ENTRIES = 200;
    if (combinedHistogram.length > MAX_HISTOGRAM_ENTRIES) {
        combinedHistogram = downsampleColumnHistogram(combinedHistogram, MAX_HISTOGRAM_ENTRIES);
    }
    const combinedTotalWeight = useOverlapWeight
        ? statsArray.reduce((acc, s) => {
            const weight = statsTotalWeight(s);
            return acc + (typeof weight === "number" && weight > 0 ? weight : 0);
        }, 0)
        : undefined;
    const result = {
        type: "number",
        count: combinedCount,
        min: combinedMin,
        max: combinedMax,
        mean: combinedMean,
        stdDev: combinedStdDev,
        histogram: combinedHistogram,
        countDistinct: histogramMap.size,
        sum: combinedSum,
        totalWeight: combinedTotalWeight,
    };
    // If exact merging wasn't possible because an input exceeded the entry
    // cap, surface that on the combined result so consumers (e.g. report
    // widgets) can warn that features spanning fragments may be
    // double-counted. Inputs that merely lack entries (legacy metrics, or
    // metrics not scoped via includedColumns) do not set these flags.
    if (statsArray.some((s) => s.entriesTruncated === true)) {
        result.entriesTruncated = true;
        result.truncationAffectedMerge = true;
    }
    return result;
}
function combineStringOrBooleanColumnValueStats(statsArray) {
    if (statsArray.length === 0) {
        return undefined;
    }
    if (statsArray.length === 1) {
        return statsArray[0];
    }
    if (statsArray.every(hasReliableColumnValueEntries)) {
        const { entries } = mergeColumnValueEntries(statsArray);
        const combined = stringOrBooleanColumnStatsFromEntries(entries);
        // Preserve the declared type when entries alone are ambiguous (e.g. all
        // fragments empty).
        if (entries.length === 0) {
            combined.type = statsArray[0].type;
        }
        const capped = capColumnValueEntries(entries);
        if (capped.entries) {
            combined.entries = capped.entries;
        }
        if (capped.entriesTruncated) {
            combined.entriesTruncated = true;
        }
        return combined;
    }
    const distinctValues = [];
    for (const stats of statsArray) {
        for (const record of stats.distinctValues) {
            const value = record[0];
            const count = record[1];
            const existing = distinctValues.find(([v]) => v === value);
            if (existing) {
                existing[1] += count;
            }
            else {
                distinctValues.push([value, count]);
            }
        }
    }
    const outputType = statsArray[0]?.type === "boolean" ? "boolean" : "string";
    const result = {
        type: outputType,
        distinctValues,
        countDistinct: distinctValues.length,
    };
    // See combineNumberColumnValueStats: propagate truncation so consumers
    // can warn that the approximate merge may double-count features.
    if (statsArray.some((s) => s.entriesTruncated === true)) {
        result.entriesTruncated = true;
        result.truncationAffectedMerge = true;
    }
    return result;
}
/**
 * Creates a unique id for a given metric dependency. Any difference in
 * MetricDependency properties, or parameters within MetricDependencyParameters
 * will result in a different hash.
 *
 * This hash is set on CompatibleSpatialMetric objects in the GraphQL API so
 * that clients can quickly determine which metrics are relevant to a given
 * report card widget.
 *
 * @param dependency The dependency to hash
 * @param overlaySourceUrls A map of table of contents item stable ids to overlay source urls. The hash will be based on the overlay source url, rather than the stable id. This way, updates to the underlying source will trigger a cache miss and trigger recalculation of the metric.
 * @returns A unique id for the dependency
 */
function hashMetricDependency(dependency, overlaySourceUrls) {
    if (dependency.stableId && overlaySourceUrls[dependency.stableId]) {
        if (!overlaySourceUrls[dependency.stableId]) {
            throw new Error(`Hashing Error. Overlay source URL not found for stable id: ${dependency.stableId}`);
        }
        dependency = {
            ...dependency,
            stableId: overlaySourceUrls[dependency.stableId],
        };
    }
    const canonical = stableSerialize(dependency);
    return fnv1a(canonical);
}
/**
 * Produces a stable, order-independent string representation of a dependency.
 * Object keys are sorted; arrays retain their order so that reordering values
 * still produces a different hash.
 */
function stableSerialize(value) {
    if (value === null || typeof value !== "object") {
        return JSON.stringify(value);
    }
    if (Array.isArray(value)) {
        const isStringArray = value.every((item) => typeof item === "string");
        const normalized = isStringArray ? [...value].sort() : value;
        return `[${normalized.map((item) => stableSerialize(item)).join(",")}]`;
    }
    const entries = Object.keys(value)
        .filter((key) => value[key] !== undefined &&
        key !== "hash" &&
        key !== "__typename")
        .sort()
        .map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`);
    return `{${entries.join(",")}}`;
}
/**
 * Fast, cross-environment 32-bit FNV-1a hash. Returns an 8-char hex string.
 */
function fnv1a(input) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
}
/**
 * Combines a list of metrics for fragments into a single metric. All metrics
 * must have the same type (e.g. total_area, count, etc.)
 * @param metrics - The metrics to combine.
 * @returns The combined metric.
 */
function combineMetricsForFragments(metrics, expectedMetricType) {
    if (metrics.length === 0) {
        if (expectedMetricType) {
            switch (expectedMetricType) {
                case "overlay_area":
                    return {
                        type: "overlay_area",
                        value: {
                            "*": 0,
                        },
                    };
                case "count":
                    return {
                        type: "count",
                        value: {
                            "*": { count: 0, uniqueIdIndex: { ranges: [], individuals: [] } },
                        },
                    };
                case "raster_stats":
                    return {
                        type: "raster_stats",
                        value: {
                            bands: [
                                {
                                    count: 0,
                                    min: 0,
                                    max: 0,
                                    mean: 0,
                                    median: 0,
                                    range: 0,
                                    histogram: [],
                                    invalid: 0,
                                    sum: 0,
                                    vrm: null,
                                    epsg: undefined,
                                },
                            ],
                        },
                    };
                case "column_values":
                    return {
                        type: "column_values",
                        value: {},
                    };
                case "total_area":
                    return {
                        type: "total_area",
                        value: 0,
                    };
                case "distance_to_shore":
                    return {
                        type: "distance_to_shore",
                        value: {
                            meters: 0,
                        },
                    };
                case "presence":
                    return {
                        type: "presence",
                        value: false,
                    };
                case "presence_table":
                    return {
                        type: "presence_table",
                        value: { values: [], exceededLimit: false },
                    };
                default:
                    throw new Error(`Unsupported metric type: ${expectedMetricType}`);
            }
        }
        else {
            throw new Error("Cannot combine empty array of metrics");
        }
    }
    // first, ensure that all metrics have the same type
    const types = new Set(metrics.map((m) => m.type));
    if (types.size > 1) {
        throw new Error(`All metrics must have the same type. Found types: ${Array.from(types).join(", ")}`);
    }
    const type = Array.from(types)[0];
    // then, combine the values
    switch (type) {
        case "raster_stats": {
            for (const metric of metrics) {
                if (metric.value.bands.length > 1) {
                    throw new Error("Multiple bands are not supported for raster_stats");
                }
            }
            const values = metrics.map((m) => m.value.bands[0]);
            return {
                type: "raster_stats",
                value: {
                    bands: [combineRasterBandStats(values)],
                },
            };
        }
        case "column_values": {
            const values = metrics.map((m) => m.value);
            return {
                type: "column_values",
                value: combineGroupedValues(values, (groupedValues) => {
                    const stats = {};
                    const attrNames = new Set();
                    // Collect all attribute names across fragments for this class key
                    for (const entry of groupedValues) {
                        if (entry && typeof entry === "object") {
                            for (const attr in entry) {
                                attrNames.add(attr);
                            }
                        }
                    }
                    for (const attr of attrNames) {
                        const attrValues = groupedValues
                            .map((entry) => entry?.[attr])
                            .filter((v) => v !== undefined);
                        if (attrValues.length === 0)
                            continue;
                        if (isNumberColumnValueStats(attrValues[0])) {
                            const combined = combineNumberColumnValueStats(attrValues);
                            if (combined) {
                                stats[attr] = combined;
                            }
                        }
                        else {
                            const combined = combineStringOrBooleanColumnValueStats(attrValues);
                            if (combined) {
                                stats[attr] = combined;
                            }
                        }
                    }
                    return stats;
                }),
            };
        }
        case "total_area": {
            const values = metrics.map((m) => m.value);
            return {
                type: "total_area",
                value: values.reduce((acc, v) => acc + v, 0),
            };
        }
        case "count": {
            const values = metrics.map((m) => m.value);
            return {
                type: "count",
                value: combineGroupedValues(values, (value) => {
                    const mergedIndexes = (0, uniqueIdIndex_1.mergeUniqueIdIndexes)(...value.map((v) => v.uniqueIdIndex));
                    const count = (0, uniqueIdIndex_1.countUniqueIds)(mergedIndexes);
                    return {
                        count,
                        uniqueIdIndex: mergedIndexes,
                    };
                }),
            };
        }
        case "distance_to_shore": {
            const values = metrics.map((m) => m.value);
            // return the closest
            const closest = values.reduce((acc, v) => {
                if (v.meters < acc.meters) {
                    return v;
                }
                return acc;
            }, values[0]);
            return {
                type: "distance_to_shore",
                value: closest,
            };
        }
        case "presence": {
            const values = metrics.map((m) => m.value);
            return {
                type: "presence",
                value: values.some((v) => v),
            };
        }
        case "presence_table": {
            const values = metrics.map((m) => m.value);
            const exceededLimit = values.some((v) => v.exceededLimit);
            const features = [];
            const ids = new Set();
            for (const value of values) {
                for (const feature of value.values) {
                    if (!ids.has(feature.__id)) {
                        ids.add(feature.__id);
                        features.push(feature);
                    }
                }
            }
            return {
                type: "presence_table",
                value: {
                    values: features,
                    exceededLimit,
                },
            };
        }
        case "overlay_area": {
            const values = metrics.map((m) => m.value);
            return {
                type: "overlay_area",
                value: combineGroupedValues(values, (v) => v.reduce((acc, v) => acc + v, 0)),
            };
        }
        default:
            throw new Error(`Unsupported metric type: ${type}`);
    }
}
function combineGroupedValues(values, combineFn) {
    const result = {};
    const keys = new Set();
    for (const value of values) {
        if (typeof value === "object" && value !== null) {
            for (const key in value) {
                if (typeof key === "string") {
                    keys.add(key);
                }
            }
        }
        else {
            throw new Error("Value is not a grouped object");
        }
    }
    for (const key of keys) {
        const groupValues = values
            .map((v) => v[key])
            .filter((v) => v !== undefined);
        if (groupValues.length > 0) {
            result[key] = combineFn(groupValues);
        }
    }
    return result;
}
function extractMetricDependenciesFromReportBody(node, dependencies = []) {
    if (typeof node !== "object" || node === null || !node.type) {
        throw new Error("Invalid node");
    }
    if ((node.type === "metric" || node.type === "blockMetric") &&
        node.attrs?.metrics) {
        const metrics = node.attrs.metrics;
        if (!Array.isArray(metrics)) {
            throw new Error("Invalid metrics");
        }
        if (metrics.length > 0) {
            if (typeof metrics[0] !== "object") {
                throw new Error("Invalid metric");
            }
            dependencies.push(...metrics);
        }
    }
    if (Array.isArray(node.content)) {
        for (const child of node.content) {
            extractMetricDependenciesFromReportBody(child, dependencies);
        }
    }
    return dependencies;
}
//# sourceMappingURL=metrics.js.map